// /settings/payment-gateway — Payment Gateway (Razorpay) — G1.2
//
// OWNER-only. Allows the tenant owner to configure Razorpay API credentials
// (encrypted at rest with AES-256-GCM), test the connection against the live
// API, and copy the webhook URL to paste into the Razorpay dashboard.
// Traceability: FR-BILL-021, NFR-039 (PCI).

import { useState, useEffect, useCallback } from 'react';
import {
  PageContainer, Button, Input, Skeleton, ErrorBoundary, useToast,
} from '@bbpos/ui';
import { paymentsService } from '@/services/payments.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FORM, cn } from '@/constants/design-tokens';
import type { GatewayCredentialsMasked } from '@bbpos/types';

const PROVIDER = 'razorpay';

export function PaymentGatewayPage() {
  const { toast } = useToast();
  const [existing, setExisting] = useState<GatewayCredentialsMasked | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  // Same origin as the admin app — every app proxies /api to the API, so this
  // is the reachable webhook URL on localhost and on each hosted domain.
  const webhookUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/v1/webhooks/razorpay`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const creds = await paymentsService.getGatewayCreds(PROVIDER);
      setExisting(creds);
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        setExisting(null);
      } else if (err instanceof ApiError) {
        toast({ variant: 'error', title: 'Failed to load credentials', description: err.message });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const handleSave = async () => {
    if (!keyId || !keySecret) {
      toast({ variant: 'warning', title: 'Enter both Key ID and Key Secret' });
      return;
    }
    setSaving(true);
    try {
      const saved = await paymentsService.upsertGatewayCreds({
        provider: PROVIDER,
        key_id: keyId.trim(),
        key_secret: keySecret.trim(),
        webhook_secret: webhookSecret.trim() || undefined,
        is_active: true,
      });
      setExisting(saved);
      setKeyId('');
      setKeySecret('');
      setWebhookSecret('');
      toast({ variant: 'success', title: 'Credentials saved', description: `Key ending in ${saved.key_id_last4}` });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Save failed';
      toast({ variant: 'error', title: 'Could not save credentials', description: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await paymentsService.testGatewayCreds(PROVIDER);
      setTestResult(res);
      if (res.ok) {
        toast({ variant: 'success', title: 'Connection OK' });
      } else {
        toast({ variant: 'warning', title: 'Test failed', description: res.error });
      }
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Test failed';
      setTestResult({ ok: false, error: msg });
      toast({ variant: 'error', title: 'Test failed', description: msg });
    } finally {
      setTesting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Deactivate Razorpay credentials? Online payments will stop working immediately.')) return;
    try {
      await paymentsService.deactivateGatewayCreds(PROVIDER);
      toast({ variant: 'success', title: 'Credentials deactivated' });
      setExisting(null);
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Deactivation failed';
      toast({ variant: 'error', title: 'Deactivation failed', description: msg });
    }
  };

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast({ variant: 'success', title: 'Webhook URL copied' });
    } catch {
      toast({ variant: 'warning', title: 'Clipboard unavailable', description: webhookUrl });
    }
  };

  return (
    <ErrorBoundary>
      <PageContainer>
        {/* Hero */}
        <div className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-brand-100 mb-1">Payments Settings</div>
              <h1 className="text-2xl font-bold text-white">Razorpay Gateway</h1>
              <p className="text-brand-100 mt-1 text-sm max-w-xl">
                Secure per-tenant API credentials for online UPI / card / wallet checkouts.
                Secrets are AES-256-GCM encrypted at rest — we store nothing you could leak.
              </p>
            </div>
            <div className="hidden md:block text-5xl leading-none opacity-80" aria-hidden>⚡</div>
          </div>
        </div>

        {/* Status + KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatusCard existing={existing} loading={loading} />
          <WebhookCard webhookUrl={webhookUrl} onCopy={copyWebhookUrl} configured={Boolean(existing?.webhook_secret_set)} />
          <TestCard existing={existing} testing={testing} onTest={handleTest} result={testResult} />
        </div>

        {/* Credentials form */}
        <div className={cn(GLASS_FORM, 'p-6 animate-slide-up')}>
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">
            {existing ? 'Rotate credentials' : 'Configure credentials'}
          </h2>
          {loading ? (
            <Skeleton className="h-48" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Key ID"
                placeholder="rzp_live_xxxxxxxxxxxxxx"
                value={keyId}
                onChange={(v: string) => setKeyId(v)}
                helperText="Find in Razorpay dashboard → Settings → API Keys"
              />
              <div className="flex flex-col">
                <Input
                  label="Key Secret"
                  type={showSecret ? 'text' : 'password'}
                  placeholder="••••••••••••••••••••••••••"
                  value={keySecret}
                  onChange={(v: string) => setKeySecret(v)}
                  helperText="Generated once when you create the key"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="text-xs text-brand-600 dark:text-brand-400 self-end mt-1 hover:underline"
                >
                  {showSecret ? 'Hide' : 'Show'} secret while typing
                </button>
              </div>
              <Input
                label="Webhook Secret (optional)"
                type="password"
                placeholder="Enter if you set one in the Razorpay dashboard webhook"
                value={webhookSecret}
                onChange={(v: string) => setWebhookSecret(v)}
                helperText="Required for signed webhook verification (recommended)"
                className="md:col-span-2"
              />
              <div className="md:col-span-2 flex flex-wrap gap-3 items-center justify-end pt-2">
                {existing && (
                  <Button variant="secondary" onClick={handleDeactivate}>
                    Deactivate
                  </Button>
                )}
                <Button onClick={handleSave} loading={saving} disabled={!keyId || !keySecret}>
                  {existing ? 'Rotate & Save' : 'Save credentials'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* PCI notice */}
        <div className={cn(GLASS, 'p-5 mt-6 border-l-4 border-success-500')}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-success-500 text-white flex items-center justify-center flex-shrink-0" aria-hidden>🛡</div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white mb-1">PCI-DSS Safe</div>
              Razorpay handles all card data directly — Orio360 stores only non-sensitive
              references like <code className="font-mono text-xs">razorpay_payment_id</code> and
              verified signatures. Card numbers, CVV, and cardholder names never touch our servers.
            </div>
          </div>
        </div>
      </PageContainer>
    </ErrorBoundary>
  );
}

function StatusCard({ existing, loading }: { existing: GatewayCredentialsMasked | null; loading: boolean }) {
  const color = existing?.is_active
    ? 'from-success-500 to-teal-500'
    : existing
      ? 'from-warning-500 to-amber-500'
      : 'from-neutral-400 to-neutral-500';
  const pillTone = existing?.is_active
    ? 'bg-success-50 text-success-700 border-success-200 dark:bg-success-900/30 dark:text-success-300'
    : existing
      ? 'bg-warning-50 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300'
      : 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300';
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-white bg-gradient-to-br', color)} aria-hidden>🔑</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Status</span>
      </div>
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : existing ? (
        <div className="space-y-2">
          <div className="text-xl font-bold tabular-nums text-neutral-900 dark:text-white">
            {existing.key_id_masked}
          </div>
          <div className="flex items-center gap-2">
            <span className={cn('inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border', pillTone)}>
              {existing.is_active ? 'Active' : 'Inactive'}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Updated {new Date(existing.updated_at).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-lg font-semibold text-neutral-900 dark:text-white">Not configured</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Online payments fall back to dev-mock in non-production.
          </div>
        </div>
      )}
    </div>
  );
}

function WebhookCard({ webhookUrl, onCopy, configured }: { webhookUrl: string; onCopy: () => void; configured: boolean }) {
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white" aria-hidden>🔔</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Webhook</span>
      </div>
      <div className="font-mono text-xs text-neutral-700 dark:text-neutral-300 break-all mb-3 p-2 rounded-md bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700">
        {webhookUrl}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className={cn(
          'inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border',
          configured
            ? 'bg-success-50 text-success-700 border-success-200 dark:bg-success-900/30 dark:text-success-300'
            : 'bg-warning-50 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300',
        )}>
          {configured ? 'Secret set' : 'Secret missing'}
        </span>
        <Button size="sm" variant="secondary" onClick={onCopy}>Copy URL</Button>
      </div>
    </div>
  );
}

function TestCard({
  existing, testing, onTest, result,
}: { existing: GatewayCredentialsMasked | null; testing: boolean; onTest: () => void; result: { ok: boolean; error?: string } | null }) {
  const last = existing?.last_tested_at
    ? `${existing.last_test_status ?? '—'} · ${new Date(existing.last_tested_at).toLocaleString('en-IN')}`
    : 'Never';
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white" aria-hidden>⚙︎</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Liveness</span>
      </div>
      <div className="text-xs text-neutral-600 dark:text-neutral-300 mb-2">Last tested: <span className="tabular-nums">{last}</span></div>
      {result && (
        <div className={cn(
          'text-xs mb-3 p-2 rounded-md border',
          result.ok
            ? 'bg-success-50 text-success-700 border-success-200 dark:bg-success-900/30 dark:text-success-300'
            : 'bg-danger-50 text-danger-700 border-danger-200 dark:bg-danger-900/30 dark:text-danger-300',
        )}>
          {result.ok ? 'Razorpay responded OK' : (result.error || 'Test failed')}
        </div>
      )}
      <Button onClick={onTest} loading={testing} disabled={!existing} className="w-full" variant={existing ? 'primary' : 'secondary'}>
        Test connection
      </Button>
    </div>
  );
}
